import React, { useCallback, useEffect, useState } from "react";
import { ChevronRight, Link2, Loader2, Radio } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { useSocial } from "../../contexts/SocialContext";
import { useAuth } from "../../contexts/AuthContext";
import { useAppState } from "../../contexts/WorkoutContext";
import * as api from "../../services/api";
import DetailSheet from "../session/generate/DetailSheet";
import HelpLink from "../help/HelpLink";
import { DeleteConfirm, Group } from "../profile/ProfileParts";
import Avatar from "./Avatar";
import InviteDialog from "./InviteDialog";

const LiveDot = () => (
  <span className="mr-1.5 inline-block h-2 w-2 rounded-full bg-red-500 align-[1px]" aria-hidden />
);

// Full class strings, so Tailwind sees each grid template.
const rowBase = "grid w-full items-center gap-2.5 bg-red-500/5 px-3 py-3 text-left";
const rowClass = `${rowBase} grid-cols-[2rem_minmax(0,1fr)_auto]`;
const linkRowClass = `${rowBase} grid-cols-[2rem_minmax(0,1fr)_1rem] hover:bg-red-500/10`;
const hostRowClass = `${rowBase} grid-cols-[2rem_minmax(0,1fr)_auto_1rem] hover:bg-red-500/10`;

/**
 * Live sessions, at the top of the feed (issue #86). This was a tab of its
 * own, mostly a paragraph of instructions above a list that the app-wide
 * banner already showed. Now the list shows only when something is live, and
 * joining by code is a sheet behind the feed's "Join live" button.
 */
export default function LiveNow() {
  const { liveSession, activeLiveSessions, loadActiveLiveSessions, joinLive, rejoinLive } = useSocial();
  const { user } = useAuth();
  const { setWorkoutPlan, setView } = useAppState();
  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState(null);
  const [hosting, setHosting] = useState(null);

  useEffect(() => {
    loadActiveLiveSessions();
    const interval = setInterval(loadActiveLiveSessions, 15000);
    return () => clearInterval(interval);
  }, [loadActiveLiveSessions]);

  const handleJoin = useCallback(
    async (code) => {
      setJoining(true);
      setJoinError(null);
      try {
        await joinLive(code);
        setView("coach");
      } catch (err) {
        setJoinError(err.message || "Couldn't join that session");
      } finally {
        setJoining(false);
      }
    },
    [joinLive, setView],
  );

  const handleRejoin = useCallback(
    (session) => {
      setJoining(true);
      rejoinLive(session)
        .then(() => {
          if (session.host_id === user?.id) setWorkoutPlan(session.plan_data);
          setView("coach");
        })
        .finally(() => setJoining(false));
    },
    [rejoinLive, setView, setWorkoutPlan, user?.id],
  );

  // In a live session, the Coach tab (LiveSessionFlow) is the one live UI;
  // this only points there.
  if (liveSession.session) {
    return (
      <Group title="Live now">
        <button type="button" onClick={() => setView("coach")} className={linkRowClass}>
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-red-500/10">
            <Radio className="h-4 w-4 text-red-500" />
          </span>
          <span className="min-w-0">
            <span className="block truncate text-sm font-medium">
              <LiveDot />
              {liveSession.session.title || "Live session"}
            </span>
            <span className="mt-0.5 block text-xs text-muted-foreground">You're in it. It runs in the Coach tab.</span>
          </span>
          <ChevronRight className="h-4 w-4 text-muted-foreground/70" />
        </button>
      </Group>
    );
  }

  if (!activeLiveSessions.length) return null;

  return (
    <>
      <Group title="Live now" footer={joinError && <span className="text-destructive">{joinError}</span>}>
        {activeLiveSessions.map((session) => {
          const mine = session.host_id === user?.id;
          const count = parseInt(session.participant_count, 10) || 0;
          const detail = `${mine ? "You're hosting" : session.host_name} · ${count} training`;
          const title = (
            <span className="min-w-0">
              <span className="block truncate text-sm font-medium">
                <LiveDot />
                {session.title || "Live workout"}
              </span>
              <span className="mt-0.5 block truncate text-xs text-muted-foreground">{detail}</span>
            </span>
          );
          if (mine) {
            return (
              <button
                key={session.id}
                type="button"
                onClick={() => setHosting(session)}
                className={hostRowClass}
              >
                <Avatar name={session.host_name} photo={session.host_photo} />
                {title}
                <span className="text-xs font-semibold text-primary">Manage</span>
                <ChevronRight className="h-4 w-4 text-muted-foreground/70" />
              </button>
            );
          }
          return (
            <div key={session.id} className={rowClass}>
              <Avatar name={session.host_name} photo={session.host_photo} />
              {title}
              {session.is_participant ? (
                <Button size="sm" variant="secondary" onClick={() => handleRejoin(session)} disabled={joining}>
                  Rejoin
                </Button>
              ) : (
                <Button size="sm" onClick={() => handleJoin(session.join_code)} disabled={joining}>
                  Join
                </Button>
              )}
            </div>
          );
        })}
      </Group>
      <HostSheet
        session={hosting}
        onClose={() => setHosting(null)}
        onRejoin={handleRejoin}
        onEnded={loadActiveLiveSessions}
      />
    </>
  );
}

/** The host's own live session: its code, back in, invite, end. */
function HostSheet({ session, onClose, onRejoin, onEnded }) {
  const [confirming, setConfirming] = useState(false);
  const [ending, setEnding] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [last, setLast] = useState(session);
  // Keep showing the session while the sheet animates closed.
  useEffect(() => {
    if (session) setLast(session);
    else setConfirming(false);
  }, [session]);
  const s = session || last;
  if (!s) return null;

  const handleEnd = async () => {
    setEnding(true);
    try {
      await api.endLiveSession(s.id);
      onClose();
      await onEnded();
    } catch (err) {
      console.error("Failed to end session:", err);
    } finally {
      setEnding(false);
    }
  };

  const count = parseInt(s.participant_count, 10) || 0;

  return (
    <>
      <DetailSheet
        open={Boolean(session) && !inviting}
        onOpenChange={(o) => !o && onClose()}
        title={`${s.title || "Live workout"}, live`}
      >
        <div className="grid justify-items-center gap-1 rounded-xl border border-border bg-background p-3 text-center">
          <span className="text-xs text-muted-foreground">Join code</span>
          <span className="font-mono text-2xl font-semibold tracking-[0.3em]">{s.join_code}</span>
          <span className="text-xs text-muted-foreground">{count} training</span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Button variant="outline" onClick={() => { onClose(); onRejoin(s); }}>
            <Radio className="mr-2 h-4 w-4" /> Back to it
          </Button>
          <Button variant="outline" onClick={() => setInviting(true)}>
            <Link2 className="mr-2 h-4 w-4" /> Invite by link
          </Button>
        </div>
        <DeleteConfirm
          action="End"
          name="the session for everyone"
          consequence="Anyone following along stops with you."
          confirming={confirming}
          onAsk={() => setConfirming(true)}
          onCancel={() => setConfirming(false)}
          onConfirm={handleEnd}
          busy={ending}
        />
      </DetailSheet>
      <InviteDialog
        open={inviting}
        onOpenChange={(o) => !o && setInviting(false)}
        kind="live"
        liveSessionId={s.id}
      />
    </>
  );
}

/** Joining by the six-character code on the host's screen. */
export function JoinLiveSheet({ open, onOpenChange }) {
  const { joinLive } = useSocial();
  const { setView } = useAppState();
  const [code, setCode] = useState("");
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setCode("");
    setError(null);
  }, [open]);

  const handleJoin = async () => {
    if (code.length < 6) return;
    setJoining(true);
    setError(null);
    try {
      await joinLive(code);
      onOpenChange(false);
      setView("coach");
    } catch (err) {
      setError(err.message || "Couldn't join that session");
    } finally {
      setJoining(false);
    }
  };

  return (
    <DetailSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Join a live session"
      footer={
        <Button className="h-12 w-full text-base font-semibold" onClick={handleJoin} disabled={joining || code.length < 6}>
          {joining && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Join
        </Button>
      }
    >
      <div className="grid gap-2">
        <label htmlFor="live-join-code" className="mb-0 text-sm text-muted-foreground">
          Type the 6-character code from the host's screen.
        </label>
        <Input
          id="live-join-code"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/[^a-z0-9]/gi, "").toUpperCase())}
          onKeyDown={(e) => e.key === "Enter" && handleJoin()}
          maxLength={6}
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          className="h-12 text-center font-mono text-xl tracking-[0.4em]"
        />
        {error && <p className="text-xs text-destructive">{error}</p>}
      </div>
      <p className="text-xs leading-snug text-muted-foreground">
        To host one, load a workout in Coach and tap Go live.{" "}
        <HelpLink topic="social#live-sessions" label="How live sessions work" />
      </p>
    </DetailSheet>
  );
}
